"use client";

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { usePathname } from "next/navigation";

export interface PageContext {
  pageName: string;
  pageType: string;
  filters?: Record<string, any>;
  selectedItems?: any[];
  stats?: Record<string, any>;
  activeTab?: string;
  tableData?: {
    totalItems?: number;
    currentPage?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: string;
  };
  searchQuery?: string;
  dateRange?: {
    from?: Date;
    to?: Date;
  };
}

export interface OpenChatRequest {
  token: number;
  prefill?: string;
}

export interface ChatContextType {
  pageContext: PageContext;
  updatePageContext: (context: Partial<PageContext>) => void;
  getContextualPrompts: () => Array<{ icon?: string; label: string; prompt: string }>;
  openChat: (prefill?: string) => void;
  openChatRequest: OpenChatRequest | null;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatContextProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const [pageContext, setPageContext] = useState<PageContext>({
    pageName: "",
    pageType: "unknown",
  });

  // Reset ALL context on every route change — prevents stale data from a previous page
  useEffect(() => {
    setPageContext({ pageName: "", pageType: "unknown" });
  }, [pathname]);

  const updatePageContext = useCallback((context: Partial<PageContext>) => {
    setPageContext((prev) => ({ ...prev, ...context }));
  }, []);

  const [openChatRequest, setOpenChatRequest] = useState<OpenChatRequest | null>(null);

  const openChat = useCallback((prefill?: string) => {
    setOpenChatRequest({ token: Date.now(), prefill });
  }, []);

  const getContextualPrompts = useCallback(() => {
    const contextInfo = [];

    if (pageContext.filters && Object.keys(pageContext.filters).length > 0) {
      contextInfo.push(`Active filters: ${JSON.stringify(pageContext.filters)}`);
    }

    if (pageContext.searchQuery) {
      contextInfo.push(`Search: "${pageContext.searchQuery}"`);
    }

    if (pageContext.activeTab) {
      contextInfo.push(`Current tab: ${pageContext.activeTab}`);
    }

    // The Help Centre shows these as plain text chips, so they carry no icon.
    // Each has to be something the Assistant can actually do — it reads data,
    // navigates and raises tickets, but cannot change anything. The design's
    // original "Set up a new agent" and "Where do I change a greeting?" both
    // asked for changes, so they were replaced.
    if (pathname?.includes("/help")) {
      return [
        { label: "What can you help me with?", prompt: "What can you help me with?" },
        { label: "Why has my credit usage gone up?", prompt: "Why has my credit usage gone up?" },
        { label: "Something is broken", prompt: "Something is broken" },
        { label: "Show me my open tickets", prompt: "Show me my open tickets" },
      ];
    }

    if (pathname?.includes("/customers")) {
      const hasFilters = pageContext.filters && Object.keys(pageContext.filters).length > 0;
      const hasSearch = !!pageContext.searchQuery;

      return [
        {
          icon: "👥",
          label: "Customer Summary",
          prompt: hasFilters || hasSearch
            ? `Give me a summary of the filtered customers. Context: ${contextInfo.join(", ")}`
            : "Give me a summary of all my customers",
        },
        {
          icon: "📊",
          label: "Analyze Data",
          prompt: hasFilters
            ? `Analyze the customer data with current filters: ${contextInfo.join(", ")}`
            : "Analyze my customer data and show insights",
        },
        {
          icon: "🔍",
          label: "Find Customer",
          prompt: "Help me find a specific customer",
        },
        {
          icon: "📞",
          label: "Recent Interactions",
          prompt: `Show me recent customer interactions${hasFilters ? " with current filters" : ""}`,
        },
      ];
    }

    if (pathname?.includes("/leads")) {
      const hasFilters = pageContext.filters && Object.keys(pageContext.filters).length > 0;
      const hasSearch = !!pageContext.searchQuery;

      return [
        {
          icon: "🎯",
          label: "Lead Summary",
          prompt: hasFilters || hasSearch
            ? `Summarize my leads with current filters. Context: ${contextInfo.join(", ")}`
            : "Summarize my leads data",
        },
        {
          icon: "📈",
          label: "Conversion Stats",
          prompt: hasFilters
            ? `Show me lead conversion statistics for current filters: ${contextInfo.join(", ")}`
            : "Show me lead conversion statistics",
        },
        {
          icon: "✨",
          label: "Top Leads",
          prompt: "Show me the most promising leads",
        },
        {
          icon: "📋",
          label: "Export Data",
          prompt: hasFilters
            ? `Help me export leads data with current filters`
            : "Help me export leads data",
        },
      ];
    }

    if (pathname?.includes("/dashboard")) {
      return [
        {
          icon: "📊",
          label: "Overview",
          prompt: `Give me a dashboard overview${pageContext.activeTab ? ` for ${pageContext.activeTab} tab` : ""}`,
        },
        {
          icon: "🔔",
          label: "Notifications",
          prompt: "Show me recent notifications",
        },
        {
          icon: "⚡",
          label: "Quick Actions",
          prompt: "What can I do from here?",
        },
        {
          icon: "📈",
          label: "Analytics",
          prompt: "Show me key metrics and analytics",
        },
      ];
    }

    if (pathname?.includes("/settings")) {
      return [
        {
          icon: "⚙️",
          label: "Settings Help",
          prompt: `Help me configure settings${pageContext.activeTab ? ` for ${pageContext.activeTab}` : ""}`,
        },
        {
          icon: "🔐",
          label: "Security",
          prompt: "Show me security settings",
        },
        {
          icon: "🔌",
          label: "Integrations",
          prompt: "Help me set up integrations",
        },
        {
          icon: "👤",
          label: "Profile",
          prompt: "Help me update my profile",
        },
      ];
    }

    if (pathname?.includes("/sales-agents/") && pathname?.split("/").length > 4) {
      const tab = pageContext.activeTab || "settings";
      const hasStats = pageContext.stats;

      if (tab === "settings") {
        return [
          {
            icon: "⚙️",
            label: "Configure Agent",
            prompt: `Help me configure this sales agent. Context: ${contextInfo.join(", ")}`,
          },
          {
            icon: "🎯",
            label: "Agent Purpose",
            prompt: "What is the purpose of this sales agent?",
          },
          {
            icon: "📝",
            label: "Update Settings",
            prompt: "Help me update the agent settings",
          },
          {
            icon: "💡",
            label: "Best Practices",
            prompt: "What are best practices for this agent?",
          },
        ];
      } else if (tab === "leads") {
        return [
          {
            icon: "📊",
            label: "Leads Summary",
            prompt: hasStats
              ? `Summarize leads for this agent. Stats: ${hasStats.total} total, ${hasStats.contacted} contacted, ${hasStats.dead} dead, ${hasStats.inLoop} in loop`
              : "Summarize leads for this sales agent",
          },
          {
            icon: "📈",
            label: "Performance",
            prompt: "How is this agent performing?",
          },
          {
            icon: "🎯",
            label: "Top Leads",
            prompt: "Show me the top leads for this agent",
          },
          {
            icon: "💬",
            label: "Recent Activity",
            prompt: "What's the recent activity for this agent?",
          },
        ];
      } else if (tab === "tools") {
        return [
          {
            icon: "🔧",
            label: "Available Tools",
            prompt: "What tools are available for this agent?",
          },
          {
            icon: "➕",
            label: "Add Tool",
            prompt: "Help me add a new tool to this agent",
          },
          {
            icon: "⚙️",
            label: "Configure Tools",
            prompt: "Help me configure the agent's tools",
          },
          {
            icon: "📖",
            label: "Tool Guide",
            prompt: "Explain how tools work for this agent",
          },
        ];
      } else if (tab === "channels") {
        return [
          {
            icon: "📱",
            label: "Channels Overview",
            prompt: "What channels is this agent using?",
          },
          {
            icon: "➕",
            label: "Add Channel",
            prompt: "Help me add a new channel",
          },
          {
            icon: "⚙️",
            label: "Configure Channels",
            prompt: "Help me configure agent channels",
          },
          {
            icon: "📊",
            label: "Channel Stats",
            prompt: "Show me channel performance stats",
          },
        ];
      }
    }

    if (pathname?.includes("/support-agents/") && pathname?.split("/").length > 4) {
      const tab = pageContext.activeTab || "settings";

      if (tab === "settings") {
        return [
          {
            icon: "⚙️",
            label: "Configure Agent",
            prompt: `Help me configure this support agent. Context: ${contextInfo.join(", ")}`,
          },
          {
            icon: "🎯",
            label: "Agent Purpose",
            prompt: "What is the purpose of this support agent?",
          },
          {
            icon: "📝",
            label: "Update Settings",
            prompt: "Help me update the agent settings",
          },
          {
            icon: "💡",
            label: "Best Practices",
            prompt: "What are best practices for this agent?",
          },
        ];
      } else if (tab === "customers") {
        return [
          {
            icon: "👥",
            label: "Customers Summary",
            prompt: "Summarize customers for this support agent",
          },
          {
            icon: "📈",
            label: "Performance",
            prompt: "How is this agent performing with customers?",
          },
          {
            icon: "🔍",
            label: "Recent Cases",
            prompt: "Show me recent customer cases for this agent",
          },
          {
            icon: "💬",
            label: "Recent Activity",
            prompt: "What's the recent activity for this agent?",
          },
        ];
      } else if (tab === "tools") {
        return [
          {
            icon: "🔧",
            label: "Available Tools",
            prompt: "What tools are available for this agent?",
          },
          {
            icon: "➕",
            label: "Add Tool",
            prompt: "Help me add a new tool to this agent",
          },
          {
            icon: "⚙️",
            label: "Configure Tools",
            prompt: "Help me configure the agent's tools",
          },
          {
            icon: "📖",
            label: "Tool Guide",
            prompt: "Explain how tools work for this agent",
          },
        ];
      } else if (tab === "channels") {
        return [
          {
            icon: "📱",
            label: "Channels Overview",
            prompt: "What channels is this agent using?",
          },
          {
            icon: "➕",
            label: "Add Channel",
            prompt: "Help me add a new channel",
          },
          {
            icon: "⚙️",
            label: "Configure Channels",
            prompt: "Help me configure agent channels",
          },
          {
            icon: "📊",
            label: "Channel Stats",
            prompt: "Show me channel performance stats",
          },
        ];
      }
    }

    return [
      {
        icon: "🚀",
        label: "Get Started",
        prompt: "What can you help me with?",
      },
      {
        icon: "📚",
        label: "Learn More",
        prompt: "Show me the documentation",
      },
      {
        icon: "❓",
        label: "Help",
        prompt: "How do I use this platform?",
      },
      {
        icon: "⚙️",
        label: "Settings",
        prompt: "Take me to settings",
      },
    ];
  }, [pathname, pageContext]);

  return (
    <ChatContext.Provider
      value={{
        pageContext: {
          ...pageContext,
          pageName: pathname || "",
        },
        updatePageContext,
        getContextualPrompts,
        openChat,
        openChatRequest,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within ChatContextProvider");
  }
  return context;
}
